import classnames from 'classnames'
import useKeyboardAccessibleGroup from 'marketing-site/lib/useKeyboardAccessibleGroup'
import React, { useRef, useState } from 'react'
import Slider from 'react-slick'
import { CTALink } from '../../elements/CTALink'
import { ImageOrAutoplayVideo } from '../../elements/ImageOrAutoplayVideo'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import {
  getColorTheme,
  mq,
  ColorTheme,
  AllSystemColors,
  AllColorValues,
  getColor,
  ColorName,
} from '../../utils'
import { AllColors as BrandColor, CTATheme } from '../../utils/constants/themes'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'

interface ILocalTheme extends ColorTheme {
  dotColor?: string
  dotColorActive?: string
  highlightColor?: string
  highlightHoverColor?: string
  highlightTextColor?: string
}

function getLocalTheme(bgColor: AllSystemColors): ILocalTheme {
  const baseColors = getColorTheme('default', bgColor)
  const localTheme = {
    ...baseColors,
    dotColor: getColor('default', AllColorValues.Slate),
    dotColorActive: getColor('default', AllColorValues.Aqua),
    highlightColor: getColor('default', AllColorValues.Aqua),
    highlightHoverColor: getColor('default', AllColorValues.White),
    highlightTextColor: getColor('default', AllColorValues.Slate),
  }
  switch (localTheme.id) {
    case AllColorValues.Stone:
      return {
        ...localTheme,
        dotColor: getColor('default', AllColorValues.UIGray),
        dotColorActive: getColor('default', AllColorValues.Slate),
        highlightColor: getColor('default', AllColorValues.White),
      }
    case AllColorValues.Sky:
      return {
        ...localTheme,
        dotColor: getColor('default', AllColorValues.Slate),
        dotColorActive: getColor('default', AllColorValues.White),
        highlightColor: getColor('default', AllColorValues.White),
      }
    case AllColorValues.White:
      return {
        ...localTheme,
        highlightHoverColor: getColor('default', AllColorValues.Stone),
        highlightColor: getColor('default', AllColorValues.Stone),
      }
    case AllColorValues.Slate:
      return {
        ...localTheme,
        CTATheme: CTATheme.TealFill,
        dotColor: getColor('default', AllColorValues.White),
        highlightColor: getColor('default', AllColorValues.White),
      }
    case AllColorValues.Blue:
      return {
        ...localTheme,
        highlightColor: getColor('default', AllColorValues.Sky),
        highlightHoverColor: getColor('default', AllColorValues.Sky),
        dotColor: getColor('default', AllColorValues.White),
        dotColorActive: getColor('default', AllColorValues.Sky),
      }
    case AllColorValues.Tangerine:
      return {
        ...localTheme,
        dotColorActive: getColor('default', AllColorValues.White),
        highlightColor: getColor('default', AllColorValues.White),
        highlightHoverColor: getColor('default', AllColorValues.Stone),
        textColor: getColor('default', AllColorValues.Slate),
      }
    default:
      return {
        ...localTheme,
      }
  }
}

export function MultiFeatureSpotlight({
  fullWidth,
  eyebrow,
  heading,
  icon,
  bgColor,
  slides,
  cta,
  isMobile,
  bodyCopy,
}: IProps): React.ReactElement {
  const [selectedSlide, setSelectedSlide] = useState(0)
  const slider = useRef<Slider>(null)
  const { containerProps, itemProps } = useKeyboardAccessibleGroup<HTMLButtonElement>()
  const bgColorTheme = getLocalTheme(bgColor)
  const compatibleColor = getColor('default', bgColor)
  const multiFeatureTheme = `multifeature-spotlight--theme theme-${
    ColorName[compatibleColor as BrandColor]
  }`
  const multiFeatureEdgeTheme = `multifeature-spotlight__edge-texture--theme theme-${
    ColorName[compatibleColor as BrandColor]
  }`
  const multiFeatureBgTheme = `multifeature-spotlight-background--theme theme-${
    ColorName[compatibleColor as BrandColor]
  }`

  function handleSlideButtonClick(index: number) {
    slider.current && slider.current.slickGoTo(index)
    setSelectedSlide(index)
  }

  function advanceSlide(index: number) {
    handleSlideButtonClick((index + 1) % slides.length)
  }

  const isActive = (index: number) => {
    return selectedSlide === index
  }

  const spacing = useSpacing()

  const outerContainerClasses = classnames('multifeature-spotlight', {
    [multiFeatureTheme]: fullWidth,
    'multifeature-spotlight__edge-texture': fullWidth,
    [multiFeatureEdgeTheme]: fullWidth,
  })
  const innerContainerClasses = classnames('multifeature-spotlight-background', {
    [multiFeatureBgTheme]: !fullWidth,
    'multifeature-spotlight__edge-texture': !fullWidth,
    [multiFeatureEdgeTheme]: !fullWidth,
    'multifeature-spotlight-background--padding': !spacing,
  })

  return (
    <div className={outerContainerClasses}>
      <div className={innerContainerClasses}>
        {icon && <img alt="" src={icon} className="multifeature-spotlight__icon" />}
        {/* Main Content Area */}
        <div className="multifeature-spotlight__main-content-area">
          <div className="multifeature-spotlight__lead">
            {/* Eyebrow */}
            {eyebrow && (
              <span className="multifeature-spotlight__eyebrow">
                <Text brand="default" size="eyebrows">
                  {eyebrow}
                </Text>
              </span>
            )}
            {/* Heading */}
            <p className="multifeature-spotlight__heading">
              <Text brand="default" size="heading-3">
                {heading}
              </Text>
            </p>
            {/* Body Copy */}
            {bodyCopy && (
              <p className="multifeature-spotlight__body-copy">
                <Text brand="default" size="heading-4">
                  {bodyCopy}
                </Text>
              </p>
            )}
          </div>

          {/* Slide Dots */}
          <ul className="multifeature-spotlight__slide-dots" aria-hidden>
            {slides.map((_, index) => {
              const slideDotClassnames = classnames(
                'multifeature-spotlight__slide-dot',
                [
                  `multifeature-spotlight__slide-dot--theme theme-${
                    ColorName[compatibleColor as BrandColor]
                  }`,
                ],
                { '--active': isActive(index) },
              )
              return (
                <li key={index}>
                  <div className={slideDotClassnames} />
                </li>
              )
            })}
          </ul>

          {/* Slides */}
          <ul className="multifeature-spotlight__slides" role="tablist" {...containerProps}>
            {slides.map((slide, index) => {
              const slideClassnames = classnames(
                'multifeature-spotlight__slide',
                [
                  `multifeature-spotlight__slide--theme theme-${
                    ColorName[compatibleColor as BrandColor]
                  }`,
                ],
                {
                  '--active': isActive(index),
                },
              )
              return (
                <li
                  key={index}
                  role="tab"
                  aria-selected={isActive(index)}
                  aria-hidden={isMobile ? !isActive(index) : false}
                >
                  <button
                    type="button"
                    className={slideClassnames}
                    onClick={
                      isMobile ? () => advanceSlide(index) : () => handleSlideButtonClick(index)
                    }
                    {...itemProps(index)}
                  >
                    <Text brand="default" size="body">
                      <RichText html={slide.text} />
                    </Text>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        {/* Image Area */}
        <div
          className={classnames('multifeature-spotlight__image-area', {
            'multifeature-spotlight__image-area--cta': cta,
          })}
        >
          {/* Product Screenshot */}
          <div className="multifeature-spotlight__mobile-slider">
            <Slider
              ref={slider}
              arrows={false}
              infinite={false}
              beforeChange={(_, newIndex) => setSelectedSlide(newIndex)}
              speed={200}
            >
              {slides.map(({ imageRef, video, videoWebm }, index) => {
                const productImageClassnames = classnames(
                  'multifeature-spotlight__product-screenshot',
                  {
                    '--active': isActive(index),
                  },
                )
                return (
                  <div
                    key={index}
                    aria-hidden={!isActive(index)}
                    className={productImageClassnames}
                  >
                    <ImageOrAutoplayVideo imageRef={imageRef} video={video} videoWebm={videoWebm} />
                  </div>
                )
              })}
            </Slider>
          </div>

          <div className="multifeature-spotlight__desktop-slider">
            {slides.map(({ imageRef, video }, index) => {
              const productImageClassnames = classnames(
                'multifeature-spotlight__product-screenshot',
                {
                  '--active': isActive(index),
                },
              )
              return (
                <div key={index} aria-hidden={!isActive(index)} className={productImageClassnames}>
                  <ImageOrAutoplayVideo imageRef={imageRef} video={video} />
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA */}
        {cta && (
          <div className="multifeature-spotlight__cta">
            <CTALink {...cta} bgColor={bgColorTheme.CTATheme} />
          </div>
        )}
      </div>
      <style jsx global>
        {`
          .slick-initialized .slick-track {
            display: flex;
            align-items: center;
          }

          .multifeature-spotlight {
            display: block; // for react-slick compatibility
            position: relative; // for icon
            background-color: unset;

            &--theme {
              background-color: ${bgColorTheme.backgroundColor};
              color: ${bgColorTheme.textColor};
            }
          }

          .multifeature-spotlight-background {
            display: block; // for react-slick compatibility
            position: relative; // for edge texture
            padding-left: 10%;
            padding-right: 10%;
            max-width: $container-max-width;

            &--padding {
              padding-top: 3.75rem;
              padding-bottom: 3.75rem;
            }

            &--theme {
              background-color: ${bgColorTheme.backgroundColor};
              color: ${bgColorTheme.textColor};
            }
          }

          .multifeature-spotlight__main-content-area {
            display: grid;
            grid-column: 2 / 14;
          }

          .multifeature-spotlight__image-area {
            display: block; // for react-slick compatibility
            max-width: 600px;

            video {
              max-height: 650px;
            }
          }

          .multifeature-spotlight__desktop-slider {
            display: none;
          }

          .multifeature-spotlight__icon {
            position: absolute;
            top: -40px;
            left: 50%;
            transform: translateX(-50%);
            width: $icon-width;
          }

          .multifeature-spotlight__lead {
            text-align: center;
          }

          .multifeature-spotlight__eyebrow {
            margin-bottom: 16px;
          }

          .multifeature-spotlight__heading {
            margin: 16px 0;
          }

          .multifeature-spotlight__body-copy {
            margin-bottom: 36px;
          }

          .multifeature-spotlight__slide-dots {
            position: relative;
            margin: 0 auto;
            padding-top: 28px;
            display: flex;

            li {
              display: inline-block;
              flex: 1;
            }
          }

          .--active {
            visibility: visible !important;
            opacity: 1 !important;
            backface-visibility: hidden;
          }

          .multifeature-spotlight__slide-dot {
            margin: 0 6px 12px;
            width: 8px;
            height: 8px;
            border-radius: 100%;
            transition: background-color 0.25s;

            &:focus {
              outline: 1px dotted $slate;
              outline-offset: 3px;
            }

            &--theme {
              background-color: ${bgColorTheme.dotColor};

              &.--active {
                background-color: ${bgColorTheme.dotColorActive};
              }
            }
          }

          .multifeature-spotlight__slides {
            display: grid;
            position: relative;
            text-align: center;
            margin: 0 auto;

            li {
              /* using grid here as an alternative to absolute */
              grid-row: 1;
              grid-column: 1;
            }
          }

          .multifeature-spotlight__slide {
            @include focusable($inlineOffset: -24px, $blockOffset: 0);

            padding: 0 0 8px;
            margin: 8px 0;
            cursor: pointer;
            position: relative;
            z-index: 1;
            transition: color, background-color, opacity 0.5s;
            visibility: hidden;
            opacity: 0;
            user-select: text;

            &:focus {
              outline: 1px dotted $slate;
              outline-offset: 3px;
            }
          }

          .multifeature-spotlight__product-screenshot > picture > img,
          video {
            border-radius: 25px;
          }

          .multifeature-spotlight
            .multifeature-spotlight-background
            .multifeature-spotlight__slides
            .multifeature-spotlight__slide
            ul {
            text-align: left;
          }

          .multifeature-spotlight__cta {
            margin-top: 40px;
            display: grid;
            justify-content: center;
          }

          @media (${mq.tablet}) {
            .multifeature-spotlight {
              display: grid;
              grid-template-columns: repeat(14, 1fr);
            }

            .multifeature-spotlight-background {
              grid-column: 1 / span 14;
              display: grid;
              grid-template-columns: repeat(14, 1fr);
              padding-left: 30px;
              padding-right: 30px;
              margin: 0 auto;
              border-radius: 0;

              &--padding {
                padding-top: 1.875rem;
                padding-bottom: 1.875rem;
              }
            }

            .multifeature-spotlight__edge-texture {
              &:after {
                position: absolute;
                content: '';
                height: 18px;
                left: 50%;
                mask-image: url(../../images/edge-texture.svg);
                mask-repeat: repeat no-repeat;
                width: 100%;
                z-index: -1;
                bottom: -12px;
                transform: translateX(-50%);
              }

              &--theme:after {
                background-color: ${bgColorTheme.backgroundColor};
              }
            }

            .multifeature-spotlight__main-content-area {
              grid-row: 1;
              grid-column: 1 / span 7;
              grid-template-columns: repeat(7, 1fr);
            }

            .multifeature-spotlight__image-area {
              grid-row: '1';
              grid-column: 8 / span 7;
              display: grid;
              grid-template-columns: repeat(7, 1fr);
              justify-items: center;
              height: unset;
            }

            .multifeature-spotlight__image-area--cta {
              grid-row: '1 / span 2';
            }

            .multifeature-spotlight__desktop-slider {
              display: block;
            }

            .multifeature-spotlight__mobile-slider {
              display: none;
            }

            .multifeature-spotlight__icon {
              grid-row: 1;
              grid-column: 2 / 3;
              position: relative;
              top: -100px;
              left: 0;
              transform: none;
            }

            .multifeature-spotlight__lead {
              grid-column: 2 / span 6;
              text-align: left;
              align-self: end;
              margin-top: 12px;
            }

            .multifeature-spotlight__heading {
              margin: 16px 0 20px;
            }

            .multifeature-spotlight__eyebrow {
              margin-bottom: 20px;
            }

            .multifeature-spotlight__product-screenshot {
              grid-row: 1;
              grid-column: -1 / -8;
              max-width: 100%;
              left: 0;
              margin-top: 0;
              opacity: 0;
              visibility: hidden;
              transition: all 0.5s;

              /* Safari polyfill for align-self: center */
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              max-height: 100%;
            }

            .multifeature-spotlight__slide-dots {
              display: none;
              width: 20px;
              height: 100px;
              text-align: right;
              grid-column: 1;
              justify-self: right;
              margin: 32px 50px 0 0;
              padding: 0 0 28px;

              li {
                display: block;
                flex: none;
              }
            }

            .multifeature-spotlight__slide-dot {
              margin: 0 auto 12px;
            }

            .multifeature-spotlight__slides {
              grid-column: 2 / span 5;
              text-align: left;
              width: 100%;
              height: 100%;

              li {
                grid-row: auto;
                grid-column: auto;
                position: relative;
              }
            }

            .multifeature-spotlight__slide {
              transition: color, background-color 0.5s;
              visibility: visible;
              opacity: 1;
              min-width: 100%;
              padding: 24px 0;

              &::before {
                content: '';
                border-radius: 8px;
                position: absolute;
                top: 0;
                left: -24px;
                width: calc(100% + 48px);
                height: 100%;
                background-color: transparent;
                z-index: -1;
                transition: all 0.5s;
              }

              &:hover,
              &:focus {
                outline: none;

                &::before {
                  outline: none;
                }
              }

              &.--active {
                &::before,
                &:hover::before,
                &:focus::before {
                  position: absolute;
                  top: 0;
                  left: -24px;
                  width: calc(100% + 48px);
                  height: 100%;
                  content: '';
                  border-radius: 8px;
                  z-index: -1;
                }
              }

              &--theme {
                &:hover,
                &:focus {
                  color: ${bgColorTheme.highlightTextColor};

                  &::before {
                    background-color: ${bgColorTheme.highlightHoverColor};
                  }
                }

                &.--active {
                  color: ${bgColorTheme.highlightTextColor};

                  &::before,
                  &:hover::before,
                  &:focus::before {
                    background-color: ${bgColorTheme.highlightColor};
                  }
                }
              }
            }

            .multifeature-spotlight__cta {
              grid-column: 1 / span 6;
              margin-top: 24px;
              grid-template-columns: repeat(6, 1fr);
              justify-content: flex-start;

              .cta-link {
                grid-column: 2 / span 5;
              }
            }
          }

          @media (${mq.laptop}) {
            .multifeature-spotlight__slides {
              grid-column: 2 / span 4;
            }

            .multifeature-spotlight-background {
              grid-template-columns: repeat(12, 1fr);
              grid-column: ${fullWidth ? '1 / span 13' : '2 / span 12'};
              padding-left: 0;
              padding-right: ${fullWidth ? `0` : `60px`};
              border-radius: 8px;

              &--padding {
                padding-top: 3.75rem;
                padding-bottom: 3.75rem;
              }
            }

            .multifeature-spotlight-background--full-width {
              border-radius: 0;
            }

            .multifeature-spotlight__main-content-area {
              grid-template-columns: repeat(6, 1fr);
              grid-column: 2 / span 5;
            }

            .multifeature-spotlight__image-area {
              grid-template-columns: repeat(6, 1fr);
              grid-column: 7 / span 7;
              display: grid;
              position: relative;
              height: unset;
            }

            .multifeature-spotlight__product-screenshot {
              grid-column: -1 / -6;
            }

            .multifeature-spotlight__cta {
              grid-column: 2 / span 4;
            }
          }
        `}
      </style>
    </div>
  )
}
